import humanizeDuration, { Unit } from "humanize-duration";
import numeral from "numeral";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { Numeral } from "./Numeral";
import { DateFormattingInfo } from "./DateFormattingInfo";
import { CURRENCY_NEGATIVE_PATTERN, CURRENCY_POSITIVE_PATTERN, NUMBER_NEGATIVE_PATTERN, NumberFormattingInfo } from "./NumberFormattingInfo";
dayjs.extend(utc);
dayjs.extend(weekOfYear);

export class Formatting implements ComponentFramework.Formatting {
    public readonly dateFormattingInfo: DateFormattingInfo;
    public readonly numberFormattingInfo: NumberFormattingInfo;
    public locale: string;
    public static _localeSingletons: Map<string, Formatting> = new Map();

    /**
     * Returns formatting instance for given locale. If not specified, locale from User Settings is used.
     *
     */
    public static Get(locale?: string): Readonly<Formatting> {
        //@ts-ignore - formatInfoCultureName does exists in both Portal and Power Apps
        const userLocale = locale ?? window.Xrm.Utility.getGlobalContext().userSettings.formatInfoCultureName;
        if (!Formatting._localeSingletons.get(userLocale)) {
            Formatting._localeSingletons.set(userLocale, new Formatting(userLocale));
        }
        return Object.freeze(Formatting._localeSingletons.get(userLocale)!);
    }

    private constructor(locale: string) {
        this.locale = locale;
        this.dateFormattingInfo = new DateFormattingInfo(locale);
        this.numberFormattingInfo = new NumberFormattingInfo(locale);
    }
    formatCurrency(value: number, precision?: number, symbol?: string): string {
        const precisionFormat = Array.from(Array(precision ?? this.numberFormattingInfo.numberDecimalDigits).keys()).map(x => "0").join('');
        const pattern = value >= 0 ? this._currencyPositivePattern : this._currencyNegativePattern;
        symbol = symbol ?? this.numberFormattingInfo.currencySymbol;
        const formatString = pattern.replace('n', `n,n.${precisionFormat}`);
        Numeral.currency({
            ...this.numberFormattingInfo,
            currencySymbol: symbol ?? this.numberFormattingInfo.currencySymbol
        });
        return numeral(value).format(formatString);
    }
    //portal only!
    formatDuration(value: number): string {
        const durationInMilliseconds = value * 60000;
        const units: Unit[] = value < 60 ? ['m'] : value >= 1440 ? ['d'] : ['h'];
        const options = {
            units: units,
            maxDecimalPoints: 2,
            language: this.locale.split('-')[0],
            decimal: this.numberFormattingInfo.numberDecimalSeparator,
            fallbacks: ["en"]
        };
        return humanizeDuration(durationInMilliseconds, options);
    }
    formatInteger(value: number): string {
        const pattern = value < 0 ? this._negativeNumberPattern : null;
        Numeral.decimal(this.numberFormattingInfo);
        let candidate = numeral(value).format(pattern!);
        //numeral has a bug where it cannot work with very big and small numbers, this is a temp fix
        // so we do not return NaN at these edge cases, however, the formatting might not be an exact match
        //TODO: resolve the issue with small numbers in numeraljs
        if (candidate === 'NaN') {
            return new Intl.NumberFormat(this.locale).format(value);
        }
        return candidate;
    }
    formatDecimal(value: number, precision?: number): string {
        precision = precision ?? this.numberFormattingInfo.numberDecimalDigits;
        const precisionFormat = Array.from(Array(precision).keys()).map(x => "n").join('');
        const pattern = value >= 0 ? this._negativeNumberPattern : null;
        let formatString = `n,n.${precisionFormat}`;
        if (pattern) {
            formatString = pattern.replace('n', formatString);
        }
        Numeral.decimal(this.numberFormattingInfo);
        let candidate = numeral(value).format(formatString);
        //numeral has a bug where it cannot work with very big and small numbers, this is a temp fix
        // so we do not return NaN at these edge cases, however, the formatting might not be an exact match
        //TODO: resolve the issue with small numbers in numeraljs
        if (candidate === 'NaN') {
            return new Intl.NumberFormat(this.locale, {
                minimumFractionDigits: precision,
                maximumFractionDigits: precision,
            }).format(value);
        }
        return candidate;
    }
    formatDateAsFilterStringInUTC(value: Date, includeTime?: boolean): string {
        const dateString = dayjs(value).format('YYYY-MM-DD');
        if (!includeTime) {
            return dateString;
        }
        //This is how Power Apps does it
        const timeString = dayjs(value).format('H:mm:ss');
        return `${dateString}T${timeString}`;
    }
    formatDateLong(value: Date): string {
        return dayjs(value).format(this.dateFormattingInfo.longDatePattern);
    }
    formatDateLongAbbreviated(value: Date): string {
        return dayjs(value).format(this.dateFormattingInfo.longDateAbbreviatedPattern);
    }
    formatDateShort(value: Date, includeTime?: boolean): string {
        if (includeTime) {
            return this.formatTime(value, 0);
        }
        return dayjs(value).format(this.dateFormattingInfo.shortDatePattern);
    }
    formatDateYearMonth(value: Date): string {
        return dayjs(value).format(this.dateFormattingInfo.yearMonthPattern);
    }
    formatLanguage(value: number): string {
        throw new Error('Not implemented!');
    }
    //dates in JavaScript are always in local time
    formatTime(value: Date, behavior: ComponentFramework.FormattingApi.Types.DateTimeFieldBehavior): string {
        if (behavior === 3) {
            return dayjs(value).utc().format(this._shortDateTimePattern);
        }
        return dayjs(value).format(this._shortDateTimePattern);

    }
    getWeekOfYear(value: Date): number {
        return dayjs(value).week();
    }
    private get _negativeNumberPattern() {
        return NUMBER_NEGATIVE_PATTERN[this.numberFormattingInfo.numberNegativePattern];
    }
    private get _currencyNegativePattern() {
        return CURRENCY_NEGATIVE_PATTERN[this.numberFormattingInfo.currencyNegativePattern];
    }
    private get _currencyPositivePattern() {
        return CURRENCY_POSITIVE_PATTERN[this.numberFormattingInfo.currencyPositivePattern];
    }
    //this is what MS does for formatTime function
    private get _shortDateTimePattern() {
        return `${this.dateFormattingInfo.shortDatePattern} ${this.dateFormattingInfo.shortTimePattern}`;
    }
}